import { isFuture } from 'date-fns';
import { createSelector } from 'reselect';

import notObject from './utils/notObject';
import notUndefined from './utils/notUndefined';

const getAssignmentsBranch = (state) => state.assignments;
const getAssignmentsFilter = (state, assignmentId) =>
  getAssignmentsBranch(state).byId[assignmentId];
const getAssignmentStatusBranch = (state) =>
  getAssignmentsBranch(state).assignmentStatus;
const getReleaseGradesStatusBranch = (state) =>
  getAssignmentsBranch(state).releaseGradesStatus;
const getGradesStatusBranch = (state) =>
  getAssignmentsBranch(state).gradesStatus;
export const getGrades = (state) => getAssignmentsBranch(state).grades;
export const getGradesFilter = (state, assignmentId) =>
  getGrades(state)[assignmentId];

const withIsUpcoming = (assignment) => ({
  ...assignment,
  isUpcoming: isFuture(new Date(assignment.startDate)),
});

export const getAssignmentsIds = createSelector(
  getAssignmentsBranch,
  ({ ids }) => ids
);

const getAssignmentsById = createSelector(
  getAssignmentsBranch,
  ({ byId }) => byId
);

export const getAssignments = createSelector(
  getAssignmentsIds,
  getAssignmentsById,
  (ids, byId) => ids.map((id) => byId[id])
);

export const getParentAssignments = createSelector(
  getAssignmentsIds,
  getAssignmentsById,
  (ids, byId) =>
    ids
      .map((id) => withIsUpcoming(byId[id]))
      .filter(({ parentAssignmentId }) => !parentAssignmentId)
);

const getAssignmentsStatus = createSelector(
  getAssignmentsBranch,
  ({ status }) => status
);

export const getAssignmentsIsLoading = createSelector(
  getAssignmentsStatus,
  ({ isLoading }) => isLoading
);

export const getAssignmentsError = createSelector(
  getAssignmentsStatus,
  ({ error }) => error
);

// single assignment selectors
export const getAssignmentById = createSelector(
  getAssignmentsFilter,
  (assignment) => assignment
);

export const getAssignmentLoading = createSelector(
  getAssignmentStatusBranch,
  ({ isLoading }) => isLoading
);

export const getAssignmentError = createSelector(
  getAssignmentStatusBranch,
  ({ error }) => error
);

// grades selectors
export const getGradesLoading = createSelector(
  getGradesStatusBranch,
  ({ isLoading }) => isLoading
);

export const getGradesError = createSelector(
  getGradesStatusBranch,
  ({ error }) => error
);

export const getGradesByAssignment = createSelector(
  getGradesFilter,
  (grades) => grades
);

// release grades selectors
const getGradesArray = createSelector(getGradesFilter, (grades) => {
  if (notObject(grades)) {
    return [];
  }

  return Object.values(grades);
});

export const getTotalGrades = createSelector(
  getGradesArray,
  (grades) => `${grades.length}`
);

export const getCurrentGrades = createSelector(getGradesArray, (grades) => {
  const currentGrades = grades.filter(({ grade }) =>
    notUndefined(grade)
  ).length;

  return `${currentGrades}`;
});

export const getIsReleasingGrades = createSelector(
  getReleaseGradesStatusBranch,
  ({ isLoading }) => isLoading
);

// student grades selectors
const getStudentGradesStatusBranch = (state) =>
  getAssignmentsBranch(state).studentGradesStatus;

export const getStudentGrades = (state, studentId) =>
  getAssignmentsBranch(state).studentGrades[studentId];

export const getStudentGradesLoading = createSelector(
  getStudentGradesStatusBranch,
  ({ isLoading }) => isLoading
);

export const getStudentGradesError = createSelector(
  getStudentGradesStatusBranch,
  ({ error }) => error
);

export const getHasStudentGrades = createSelector(
  getStudentGrades,
  (grades = {}) => Object.keys(grades).length > 0
);

export const getCreateAssignmentStatusBranch = (state) =>
  getAssignmentsBranch(state).createAssignmentStatus;

export const getIsCreatingAssignment = createSelector(
  getCreateAssignmentStatusBranch,
  ({ isCreating }) => isCreating
);

export const getUpdateAssignmentStatusBranch = (state) =>
  getAssignmentsBranch(state).updateAssignmentStatus;

export const getIsUpdatingAssignment = createSelector(
  getUpdateAssignmentStatusBranch,
  ({ isUpdating }) => isUpdating
);

// grading manual assignment

const getGradeManualAssignmentStatusBranch = (state) =>
  getAssignmentsBranch(state).gradeManualAssignmentStatus;

const getAssignmentGradeStatus = (state, assignmentId) =>
  getGradeManualAssignmentStatusBranch(state)[assignmentId];

const getIsSavingGradeStatus = (gradesStatus = {}) =>
  !!(Object.values(gradesStatus).find(({ isLoading }) => isLoading) || {})
    .isLoading;

const byLatestSavedAt = (latestSavedAt, { savedAt }) => {
  if (!latestSavedAt && savedAt) return savedAt;

  return savedAt > latestSavedAt ? savedAt : latestSavedAt;
};

const getLastSavedAtGradeStatus = (gradesStatus = {}) =>
  Object.values(gradesStatus).reduce(byLatestSavedAt, undefined);

export const getIsSavingGradeManualAssignment = createSelector(
  getAssignmentGradeStatus,
  getIsSavingGradeStatus
);

export const getManualGradeSavingErrors = createSelector(
  getAssignmentGradeStatus,
  (gradesStatus = {}) =>
    Object.entries(gradesStatus).reduce(
      (accum, [studentId, { error }]) => ({
        ...accum,
        ...(!!error && { [studentId]: error }),
      }),
      {}
    )
);

export const getLastSavedAtGradeManualAssignment = createSelector(
  getAssignmentGradeStatus,
  getLastSavedAtGradeStatus
);

const getImportManualAssignmentGradesBranch = (state) =>
  getAssignmentsBranch(state).importManualAssignmentGradesStatus;

const getImportManualAssignmentGradesStatus = (state, assignmentId) =>
  getImportManualAssignmentGradesBranch(state)[assignmentId];

export const getIsUploadingManualAssignmentGrades = createSelector(
  getImportManualAssignmentGradesStatus,
  ({ isLoading = false } = {}) => isLoading
);
